import { ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useRef } from "react";
import { useSelector } from "react-redux";

interface DataToolbarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  dateRange: [string | null, string | null];
  setDateRange: (range: [string | null, string | null]) => void;
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
}

const DataToolbar: React.FC<DataToolbarProps> = ({
  searchTerm,
  setSearchTerm,
  dateRange,
  setDateRange,
  rowsPerPage,
  setRowsPerPage,
  currentPage,
  totalPages,
  setCurrentPage,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const RoleID = useSelector(
    (state: any) => state?.module?.modules?.[0]?.ROLEID
  );
  return (
    <Box
      sx={{
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
        justifyContent: "space-between",
        borderBottom: 1,
        borderColor: "grey.200",
        backgroundColor: "#f5f5f5",
      }}
    >
      {/* Search */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 2,
          px: 1,
          py: 0.5,
          bgcolor: "background.paper",
        }}
      >
        <Search sx={{ fontSize: 18, color: "grey.600", mr: 1 }} />
        <InputBase
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ fontSize: "0.875rem", width: 200 }}
        />
      </Box>

      {/* Right Controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Date Picker */}

        {RoleID === 2 && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box ref={anchorRef} >
              <DatePicker
                label="Start"
                value={dateRange[0] ? dayjs(dateRange[0]) : null}
                onChange={(newDate) =>
                  setDateRange([
                    newDate ? newDate.format("YYYY-MM-DD") : null,
                    dateRange[1],
                  ])
                }
                slotProps={{
                  textField: {
                    size: "small",

                    sx: {
                      width: 150,
                      backgroundColor: "white",
                      mr: 2,

                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        fontSize: "13px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "12px",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "18px",
                      },
                    },
                  },
                }}
              />

              <DatePicker
                label="End"
                value={dateRange[1] ? dayjs(dateRange[1]) : null}
                minDate={dateRange[0] ? dayjs(dateRange[0]) : undefined}
                onChange={(newDate) =>
                  setDateRange([
                    dateRange[0],
                    newDate ? newDate.format("YYYY-MM-DD") : null,
                  ])
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: 150,
                      backgroundColor: "white",

                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        fontSize: "13px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "12px",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "18px",
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        )}

        {/* Show by Dropdown */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#4A4A4A" }}>
            Show by
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            size="small"
            sx={{
              height: 32,
              minWidth: 70,
              fontSize: "14px",
              borderRadius: "6px",
              background: "#fff",
              px: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3f1f1fff",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#BDBDBD",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#4A4A4A",
              },
              "& .MuiSelect-icon": { fontSize: 18, color: "#4D4D4D" },
            }}
          >
            {[10, 25, 50].map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Pagination */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            size="small"
          >
            <ChevronLeft sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: 14,
              bgcolor: "white",
              padding: "5px 10px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            {currentPage} of {totalPages}
          </Typography>
          <IconButton
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            size="small"
          >
            <ChevronRight sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default DataToolbar;
